import { useState, useEffect } from 'react';
import { Calendar, Clock, TrendingUp, Users, BarChart3, Target, Award, ChevronRight, Search, Plus, Star, CheckCircle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { cn } from "@/lib/utils";
import { CandidateSelector, InterviewForm, SkillsRadarChart } from './index';
import { InterviewResults } from './InterviewResults';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../hooks/useToast';
import { supabase } from '../../lib/supabase';
import { UserActionsService } from '../../services/userActions';

interface Candidate {
  id: string;
  name: string;
  firstName: string;
  lastName: string;
  email: string;
  position: string;
  jobDescription: string;
  resumeUrl?: string;
  analysisScore?: number;
  lastAnalyzed?: Date;
  status: 'new' | 'interviewed' | 'hired' | 'rejected';
  skills?: string[];
  relevanceScore?: number;
  verdict?: string;
  technicalSkills?: Array<{
    skill: string;
    proficiency: string;
    evidence: string;
  }>;
  interviewQuestions?: {
    technical: string[];
    behavioral: string[];
    cultural: string[];
    riskFactors: string[];
    redFlags: string[];
    greenFlags: string[];
  };
  projectId?: string;
  analysisId?: string;
  interviewPerspectives?: Record<string, string>;
}

interface Interview {
  id: string;
  candidateId: string;
  candidateName: string;
  firstName: string;
  lastName: string;
  candidateEmail: string;
  position: string;
  jobDescription?: string;
  type: string;
  status: string;
  overallRating: number;
  recommendation: string;
  skills: {
    technical: number;
    communication: number;
    problemSolving: number;
    teamwork: number;
    leadership: number;
    [key: string]: number;
  };
  notes: string;
  createdAt: string;
  updatedAt: string;
  projectId?: string;
  analysisId?: string;
  analysisScore?: number;
  verdict?: string;
}

interface InterviewDashboardProps {
  projectId?: string;
}

export function InterviewDashboard({ projectId }: InterviewDashboardProps) {
  const { user } = useAuth();
  const { addToast } = useToast();
  
  const [interviews, setInterviews] = useState<Interview[]>([]);
  const [selectedInterview, setSelectedInterview] = useState<Interview | null>(null);
  const [selectedCandidate, setSelectedCandidate] = useState<Candidate | undefined>(undefined);
  const [isLoading, setIsLoading] = useState(true);
  const [showInterviewForm, setShowInterviewForm] = useState(false);
  const [showInterviewResults, setShowInterviewResults] = useState(false);
  const [showCandidateSelector, setShowCandidateSelector] = useState(false);
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState<string>('overview');

  useEffect(() => {
    loadInterviewData();
    
    // Track interview management feature usage
    const trackFeatureUsage = async () => {
      try {
        await UserActionsService.trackFeatureUsage('interview-management', {
          project_id: projectId,
          accessed_at: new Date().toISOString()
        });
      } catch (error) {
        console.error('Error tracking interview management usage:', error);
      }
    };

    if (user) {
      trackFeatureUsage();
    }
  }, [user, projectId]);

  const loadInterviewData = async () => {
    if (!user) return;
    
    setIsLoading(true);
    try {
      // Load interviews
      const { data: interviewsData, error: interviewsError } = await supabase
        .from('interviews')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (interviewsError) throw interviewsError;

      // Process interviews data
      const processedInterviews: Interview[] = interviewsData?.map(interview => ({
        id: interview.id,
        candidateId: interview.candidate_id || '',
        candidateName: interview.candidate_name || `${interview.first_name || ''} ${interview.last_name || ''}`.trim() || 'Unknown Candidate',
        firstName: interview.first_name || '',
        lastName: interview.last_name || '',
        candidateEmail: interview.candidate_email || '',
        position: interview.position || 'Not specified',
        jobDescription: interview.job_description || '',
        type: interview.interview_type || 'technical',
        status: interview.status || 'scheduled',
        overallRating: interview.overall_rating || 5,
        recommendation: interview.recommendation || 'maybe',
        skills: interview.skills_rating || {
          technical: 5,
          communication: 5,
          problemSolving: 5,
          teamwork: 5,
          leadership: 5,
          adaptability: 5,
          creativity: 5,
          timeManagement: 5
        },
        notes: interview.notes || '',
        createdAt: interview.created_at || new Date().toISOString(),
        updatedAt: interview.updated_at || new Date().toISOString(),
        projectId: interview.project_id || null,
        analysisId: interview.analysis_id || null,
        analysisScore: interview.analysis_score || null,
        verdict: interview.verdict || null
      })) || [];

      setInterviews(processedInterviews);
    } catch (error) {
      console.error('Error loading interview data:', error);
      addToast('Failed to load interview data', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const handleInterviewSave = async (interviewData: any) => {
    try {
      let candidateId = null; // Always start with null for safety

      // Only try to set candidate_id if we have a valid selectedCandidate with an ID
      if (selectedCandidate?.id) {
        // Verify the candidate exists in the database
        try {
          const { data: existingCandidate, error: checkError } = await supabase
            .from('candidates')
            .select('id')
            .eq('id', selectedCandidate.id)
            .eq('user_id', user?.id)
            .maybeSingle();

          if (!checkError && existingCandidate) {
            candidateId = selectedCandidate.id;
            console.log('✅ Using existing candidate ID:', candidateId);
          } else {
            console.log('⚠️ Candidate ID not found in database, continuing without candidate_id');
          }
        } catch (error) {
          console.log('⚠️ Error checking candidate, continuing without candidate_id:', error);
        }
      }

      console.log('🎯 Final candidateId for interview:', candidateId);

      if (selectedInterview) {
        // Update existing interview
        const { error } = await supabase
          .from('interviews')
          .update({
            candidate_name: interviewData.candidateName || `${interviewData.firstName || ''} ${interviewData.lastName || ''}`.trim(),
            first_name: interviewData.firstName,
            last_name: interviewData.lastName,
            candidate_email: interviewData.candidateEmail,
            position: interviewData.position || 'Not specified',
            job_description: interviewData.jobDescription,
            interview_type: interviewData.type || 'technical',
            status: interviewData.status || 'scheduled',
            overall_rating: interviewData.overallRating || 5,
            skills_rating: interviewData.skills || {
              technical: 5,
              communication: 5,
              problemSolving: 5,
              teamwork: 5,
              leadership: 5,
              adaptability: 5,
              creativity: 5,
              timeManagement: 5
            },
            notes: interviewData.notes || '',
            recommendation: interviewData.recommendation || 'maybe',
            project_id: interviewData.projectId,
            analysis_id: interviewData.analysisId,
            analysis_score: interviewData.analysisScore,
            verdict: interviewData.verdict,
            updated_at: new Date().toISOString()
          })
          .eq('id', selectedInterview.id);

        if (error) throw error;
        addToast('Interview updated successfully!', 'success');

        // Track interview update
        try {
          await UserActionsService.trackFeatureUsage('interview-updated', {
            interview_id: selectedInterview.id,
            candidate_name: interviewData.candidateName,
            status: interviewData.status,
            project_id: interviewData.projectId,
            updated_at: new Date().toISOString()
          });
        } catch (error) {
          console.error('Error tracking interview update:', error);
        }
      } else {
        // Create new interview
        const interviewPayload: any = {
          user_id: user?.id,
          organization_id: null,
          candidate_id: candidateId, // Will be null if not valid
          candidate_name: interviewData.candidateName || `${interviewData.firstName || ''} ${interviewData.lastName || ''}`.trim() || 'Unknown Candidate',
          first_name: interviewData.firstName || '',
          last_name: interviewData.lastName || '',
          candidate_email: interviewData.candidateEmail || selectedCandidate?.email || '',
          position: interviewData.position || 'Not specified',
          job_description: interviewData.jobDescription || '',
          interview_type: interviewData.type || 'technical',
          status: interviewData.status || 'scheduled',
          interview_date: interviewData.date || new Date().toISOString().split('T')[0],
          scheduled_at: interviewData.scheduledAt || null, // Now nullable
          overall_rating: interviewData.overallRating || 5,
          skills_rating: interviewData.skills || {
            technical: 5,
            communication: 5,
            problemSolving: 5,
            teamwork: 5,
            leadership: 5,
            adaptability: 5,
            creativity: 5,
            timeManagement: 5
          },
          notes: interviewData.notes || '',
          recommendation: interviewData.recommendation || 'maybe',
          project_id: interviewData.projectId || selectedCandidate?.projectId || null,
          analysis_id: interviewData.analysisId || selectedCandidate?.analysisId || null,
          analysis_score: interviewData.analysisScore || selectedCandidate?.analysisScore || null,
          verdict: interviewData.verdict || selectedCandidate?.verdict || null,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        };

        console.log('🚀 Inserting interview with payload:', interviewPayload);

        const { error } = await supabase
          .from('interviews')
          .insert(interviewPayload);

        if (error) {
          console.error('❌ Database error details:', error);
          throw error;
        }
        addToast('Interview created successfully!', 'success');

        // Track interview creation
        try {
          await UserActionsService.trackFeatureUsage('interview-created', {
            candidate_name: interviewData.candidateName,
            position: interviewData.position,
            interview_type: interviewData.type,
            status: interviewData.status,
            project_id: interviewData.projectId,
            created_at: new Date().toISOString()
          });
        } catch (error) {
          console.error('Error tracking interview creation:', error);
        }
      }

      await loadInterviewData();
      setShowInterviewForm(false);
      setSelectedInterview(null);
      setSelectedCandidate(undefined);
    } catch (error) {
      console.error('Error saving interview:', error);
      addToast('Failed to save interview', 'error');
    }
  };

  const handleNewInterview = () => {
    setSelectedCandidate(undefined);
    setSelectedInterview(null);
    setShowCandidateSelector(true);
  };

  const handleCandidateSelected = (candidate: Candidate) => {
    console.log('🎯 CANDIDATE SELECTED:', {
      id: candidate.id,
      name: candidate.name,
      firstName: candidate.firstName,
      lastName: candidate.lastName,
      position: candidate.position,
      email: candidate.email,
      analysisScore: candidate.analysisScore,
      verdict: candidate.verdict,
      hasInterviewPerspectives: !!candidate.interviewPerspectives,
      hasInterviewQuestions: !!candidate.interviewQuestions,
      allKeys: Object.keys(candidate)
    });
    
    setSelectedCandidate(candidate);
    setShowCandidateSelector(false);
    setShowInterviewForm(true);
  };

  const handleInterviewClick = (interview: Interview) => {
    console.log('🎯 INTERVIEW CLICKED:', {
      id: interview.id,
      candidateName: interview.candidateName,
      status: interview.status,
      recommendation: interview.recommendation
    });
    
    setSelectedInterview(interview);
    setSelectedCandidate(undefined); // Clear selected candidate when viewing existing interview
    
    // For completed interviews, show results only. For others, show the form
    if (interview.status === 'completed') {
      setShowInterviewResults(true);
    } else {
      setShowInterviewForm(true);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed': return 'bg-green-50 text-green-700 border-green-200';
      case 'scheduled': return 'bg-yellow-50 text-yellow-700 border-yellow-200';
      case 'in_progress': return 'bg-blue-50 text-blue-700 border-blue-200';
      case 'cancelled': return 'bg-red-50 text-red-700 border-red-200';
      default: return 'bg-gray-50 text-gray-700 border-gray-200';
    }
  };

  const getRecommendationColor = (recommendation: string) => {
    switch (recommendation) {
      case 'strong_hire': return 'bg-green-50 text-green-700 border-green-200';
      case 'hire': return 'bg-blue-50 text-blue-700 border-blue-200';
      case 'maybe': return 'bg-yellow-50 text-yellow-700 border-yellow-200';
      case 'no_hire': return 'bg-red-50 text-red-700 border-red-200';
      default: return 'bg-gray-50 text-gray-700 border-gray-200';
    }
  };

  const filteredInterviews = interviews.filter(interview => {
    const matchesStatus = filterStatus === 'all' || interview.status === filterStatus;
    const matchesSearch = interview.candidateName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         interview.position.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesStatus && matchesSearch;
  });

  const overviewStats = {
    totalInterviews: interviews.length,
    completedInterviews: interviews.filter(i => i.status === 'completed').length,
    scheduledInterviews: interviews.filter(i => i.status === 'scheduled').length,
    averageRating: interviews.length > 0 
      ? interviews.reduce((sum, i) => sum + i.overallRating, 0) / interviews.length 
      : 0,
    strongHireRate: interviews.length > 0
      ? (interviews.filter(i => i.recommendation === 'strong_hire').length / interviews.length) * 100
      : 0
  };

  if (isLoading) {
    return (
      <div className="space-y-8">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {[...Array(4)].map((_, i) => (
            <Card key={i} className="bg-white rounded-xl shadow-sm border border-slate-200">
              <CardContent className="p-6">
                <Skeleton className="h-4 w-24 mb-4" />
                <Skeleton className="h-8 w-16 mb-2" />
                <Skeleton className="h-3 w-20" />
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6">
        <div className="flex items-center gap-4">
          <div className="h-12 w-12 bg-slate-900 rounded-xl flex items-center justify-center">
            <Users className="h-6 w-6 text-white" />
          </div>
          <div className="space-y-1">
            <h1 className="text-2xl lg:text-3xl font-bold text-slate-900">Interview Management</h1>
            <p className="text-slate-600">Streamlined interview process and candidate assessment</p>
          </div>
        </div>
        <Button 
          onClick={handleNewInterview}
          className="bg-slate-900 hover:bg-slate-800 text-white rounded-lg"
        >
          <Plus className="h-4 w-4 mr-2" />
          New Interview
        </Button>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-200 hover:shadow-md transition-shadow cursor-pointer">
          <div className="flex items-center justify-between mb-4">
            <div className="space-y-1">
              <p className="text-slate-600 text-sm font-medium">Total Interviews</p>
              <p className="text-3xl font-bold text-slate-900">{overviewStats.totalInterviews}</p>
              <p className="text-slate-500 text-xs">All interviews scheduled</p>
            </div>
            <div className="bg-slate-100 p-3 rounded-lg">
              <Calendar className="h-6 w-6 text-slate-600" />
            </div>
          </div>
          <div className="flex items-center text-sm text-slate-600">
            <span>View all interviews</span>
            <ChevronRight className="h-4 w-4 ml-1" />
          </div>
        </div>

        <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-200 hover:shadow-md transition-shadow cursor-pointer">
          <div className="flex items-center justify-between mb-4">
            <div className="space-y-1">
              <p className="text-slate-600 text-sm font-medium">Completed</p>
              <p className="text-3xl font-bold text-slate-900">{overviewStats.completedInterviews}</p>
              <p className="text-slate-500 text-xs">Finished assessments</p>
            </div>
            <div className="bg-slate-100 p-3 rounded-lg">
              <CheckCircle className="h-6 w-6 text-slate-600" />
            </div>
          </div>
          <div className="flex items-center text-sm text-slate-600">
            <span>View completed</span>
            <ChevronRight className="h-4 w-4 ml-1" />
          </div>
        </div>

        <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-200 hover:shadow-md transition-shadow cursor-pointer">
          <div className="flex items-center justify-between mb-4">
            <div className="space-y-1">
              <p className="text-slate-600 text-sm font-medium">Average Rating</p>
              <p className="text-3xl font-bold text-slate-900">{overviewStats.averageRating.toFixed(1)}</p>
              <p className="text-slate-500 text-xs">Out of 10.0</p>
            </div>
            <div className="bg-slate-100 p-3 rounded-lg">
              <Star className="h-6 w-6 text-slate-600" />
            </div>
          </div>
          <div className="flex items-center text-sm text-slate-600">
            <span>Performance metrics</span>
            <ChevronRight className="h-4 w-4 ml-1" />
          </div>
        </div>

        <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-200 hover:shadow-md transition-shadow cursor-pointer">
          <div className="flex items-center justify-between mb-4">
            <div className="space-y-1">
              <p className="text-slate-600 text-sm font-medium">Strong Hire Rate</p>
              <p className="text-3xl font-bold text-slate-900">{overviewStats.strongHireRate.toFixed(0)}%</p>
              <p className="text-slate-500 text-xs">Recommendation rate</p>
            </div>
            <div className="bg-slate-100 p-3 rounded-lg">
              <Award className="h-6 w-6 text-slate-600" />
            </div>
          </div>
          <div className="flex items-center text-sm text-slate-600">
            <span>View insights</span>
            <ChevronRight className="h-4 w-4 ml-1" />
          </div>
        </div>
      </div>

      {/* Main Content */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-8">
        <div className="bg-white rounded-xl p-2 shadow-sm border border-slate-200">
          <TabsList className="grid w-full grid-cols-3 bg-transparent gap-1">
            <TabsTrigger 
              value="overview" 
              className="flex items-center gap-3 px-4 py-3 rounded-lg data-[state=active]:bg-slate-900 data-[state=active]:text-white transition-all duration-200 text-slate-600 hover:text-slate-900 hover:bg-slate-100"
            >
              <BarChart3 className="h-4 w-4" />
              <span className="font-medium text-sm">Overview</span>
            </TabsTrigger>
            <TabsTrigger 
              value="interviews" 
              className="flex items-center gap-3 px-4 py-3 rounded-lg data-[state=active]:bg-slate-900 data-[state=active]:text-white transition-all duration-200 text-slate-600 hover:text-slate-900 hover:bg-slate-100"
            >
              <Calendar className="h-4 w-4" />
              <span className="font-medium text-sm">Interviews</span>
            </TabsTrigger>
            <TabsTrigger 
              value="analytics" 
              className="flex items-center gap-3 px-4 py-3 rounded-lg data-[state=active]:bg-slate-900 data-[state=active]:text-white transition-all duration-200 text-slate-600 hover:text-slate-900 hover:bg-slate-100"
            >
              <TrendingUp className="h-4 w-4" />
              <span className="font-medium text-sm">Analytics</span>
            </TabsTrigger>
          </TabsList>
        </div>

        <TabsContent value="overview" className="space-y-6">
          <div className="grid grid-cols-1 xl:grid-cols-3 gap-8">
            {/* Recent Interviews */}
            <div className="xl:col-span-2">
              <Card className="bg-white rounded-xl shadow-sm border border-slate-200">
                <CardHeader className="pb-4">
                  <CardTitle className="text-lg font-semibold text-slate-900 flex items-center">
                    <Clock className="h-5 w-5 mr-2 text-slate-600" />
                    Recent Interviews
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {filteredInterviews.slice(0, 5).map((interview) => (
                    <div key={interview.id} className="flex items-center justify-between p-4 rounded-lg bg-slate-50 hover:bg-slate-100 transition-colors cursor-pointer">
                      <div className="flex-1">
                        <h4 className="font-medium text-slate-900">{interview.candidateName}</h4>
                        <p className="text-sm text-slate-600">{interview.position}</p>
                      </div>
                      <div className="flex items-center gap-3">
                        <Badge className={cn("text-xs font-medium border", getStatusColor(interview.status))}>
                          {interview.status.replace('_', ' ')}
                        </Badge>
                        {interview.overallRating > 0 && (
                          <div className="flex items-center gap-1">
                            <Star className="h-4 w-4 text-yellow-500 fill-current" />
                            <span className="text-sm font-medium text-slate-700">{interview.overallRating.toFixed(1)}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                  {filteredInterviews.length === 0 && (
                    <div className="text-center py-8">
                      <Calendar className="h-12 w-12 text-slate-400 mx-auto mb-4" />
                      <p className="text-slate-600">No interviews yet</p>
                      <p className="text-sm text-slate-500 mt-1">Create your first interview to get started</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Top Performers */}
            <Card className="bg-white rounded-xl shadow-sm border border-slate-200">
              <CardHeader className="pb-4">
                <CardTitle className="text-lg font-semibold text-slate-900 flex items-center">
                  <Target className="h-5 w-5 mr-2 text-slate-600" />
                  Top Performers
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {interviews
                  .filter(i => i.status === 'completed')
                  .sort((a, b) => b.overallRating - a.overallRating)
                  .slice(0, 5)
                  .map((interview) => (
                    <div key={interview.id} className="flex items-center justify-between p-3 rounded-lg bg-slate-50">
                      <div className="flex-1">
                        <h4 className="font-medium text-slate-900">{interview.candidateName}</h4>
                        <p className="text-sm text-slate-600">{interview.position}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge className={cn("text-xs font-medium border", getRecommendationColor(interview.recommendation))}>
                          {interview.recommendation.replace('_', ' ')}
                        </Badge>
                        <div className="flex items-center gap-1">
                          <Star className="h-4 w-4 text-yellow-500 fill-current" />
                          <span className="font-bold text-slate-900">{interview.overallRating.toFixed(1)}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                {interviews.filter(i => i.status === 'completed').length === 0 && (
                  <div className="text-center py-8">
                    <Award className="h-12 w-12 text-slate-400 mx-auto mb-4" />
                    <p className="text-slate-600">No completed interviews</p>
                    <p className="text-sm text-slate-500 mt-1">Complete interviews to see top performers</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="interviews" className="space-y-6">
          {/* Filters */}
          <Card className="bg-white rounded-xl shadow-sm border border-slate-200">
            <CardContent className="p-6">
              <div className="flex flex-col sm:flex-row gap-4">
                <div className="flex-1">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
                    <Input
                      placeholder="Search candidates or positions..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-10 border-slate-200 focus:border-slate-400 focus:ring-slate-400"
                    />
                  </div>
                </div>
                <Select value={filterStatus} onValueChange={setFilterStatus}>
                  <SelectTrigger className="w-40 border-slate-200">
                    <SelectValue placeholder="Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Status</SelectItem>
                    <SelectItem value="scheduled">Scheduled</SelectItem>
                    <SelectItem value="in_progress">In Progress</SelectItem>
                    <SelectItem value="completed">Completed</SelectItem>
                    <SelectItem value="cancelled">Cancelled</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>

          {/* Interviews List */}
          <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
            {filteredInterviews.map((interview) => (
              <Card 
                key={interview.id} 
                className="bg-white rounded-xl shadow-sm border border-slate-200 hover:shadow-md transition-shadow cursor-pointer"
                onClick={() => handleInterviewClick(interview)}
              >
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between">
                    <div>
                      <CardTitle className="text-lg font-semibold text-slate-900">{interview.candidateName}</CardTitle>
                      <p className="text-sm text-slate-600 mt-1">{interview.position}</p>
                    </div>
                    <Badge className={cn("text-xs font-medium border", getStatusColor(interview.status))}>
                      {interview.status.replace('_', ' ')}
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-slate-600">Type:</span>
                    <span className="font-medium text-slate-900 capitalize">{interview.type}</span>
                  </div>
                  
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-slate-600">Created:</span>
                    <span className="font-medium text-slate-900">
                      {new Date(interview.createdAt).toLocaleDateString()}
                    </span>
                  </div>
                  
                  {interview.status === 'completed' && (
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-slate-600">Overall Rating:</span>
                        <div className="flex items-center gap-1">
                          <Star className="h-4 w-4 text-yellow-500 fill-current" />
                          <span className="font-bold text-slate-900">{interview.overallRating.toFixed(1)}</span>
                        </div>
                      </div>
                      
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-slate-600">Recommendation:</span>
                        <Badge className={cn("text-xs font-medium border", getRecommendationColor(interview.recommendation))}>
                          {interview.recommendation.replace('_', ' ').toUpperCase()}
                        </Badge>
                      </div>
                      
                      <div className="w-full h-40 flex items-center justify-center">
                        <div className="w-full max-w-[200px]">
                          <SkillsRadarChart skills={interview.skills} size="sm" />
                        </div>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
            
            {filteredInterviews.length === 0 && (
              <div className="col-span-full">
                <Card className="bg-white rounded-xl shadow-sm border border-slate-200">
                  <CardContent className="text-center py-12">
                    <Calendar className="h-16 w-16 text-slate-400 mx-auto mb-4" />
                    <h3 className="text-lg font-semibold text-slate-900 mb-2">
                      {interviews.length === 0 ? 'No Interviews Yet' : 'No Matching Interviews'}
                    </h3>
                    <p className="text-slate-600 mb-6">
                      {interviews.length === 0 
                        ? 'Get started by creating your first interview' 
                        : 'Try adjusting your search or filter criteria'
                      }
                    </p>
                    {interviews.length === 0 && (
                      <Button 
                        onClick={handleNewInterview}
                        className="bg-slate-900 hover:bg-slate-800 text-white"
                      >
                        <Plus className="h-4 w-4 mr-2" />
                        Create First Interview
                      </Button>
                    )}
                  </CardContent>
                </Card>
              </div>
            )}
          </div>
        </TabsContent>



        <TabsContent value="analytics" className="space-y-6">
          {/* Analytics Overview Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <Card className="bg-white rounded-xl shadow-sm border border-slate-200">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-slate-600">Success Rate</p>
                    <p className="text-2xl font-bold text-slate-900">
                      {interviews.length > 0 
                        ? Math.round((interviews.filter(i => i.recommendation === 'hire' || i.recommendation === 'strong_hire').length / interviews.length) * 100)
                        : 0}%
                    </p>
                    <p className="text-xs text-slate-500">Hire recommendations</p>
                  </div>
                  <div className="h-8 w-8 bg-green-100 rounded-lg flex items-center justify-center">
                    <TrendingUp className="h-4 w-4 text-green-600" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-white rounded-xl shadow-sm border border-slate-200">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-slate-600">Avg. Interview Score</p>
                    <p className="text-2xl font-bold text-slate-900">
                      {interviews.length > 0 
                        ? (interviews.reduce((sum, i) => sum + i.overallRating, 0) / interviews.length).toFixed(1)
                        : '0.0'}
                    </p>
                    <p className="text-xs text-slate-500">Out of 10.0</p>
                  </div>
                  <div className="h-8 w-8 bg-blue-100 rounded-lg flex items-center justify-center">
                    <Star className="h-4 w-4 text-blue-600" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-white rounded-xl shadow-sm border border-slate-200">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-slate-600">Completion Rate</p>
                    <p className="text-2xl font-bold text-slate-900">
                      {interviews.length > 0 
                        ? Math.round((interviews.filter(i => i.status === 'completed').length / interviews.length) * 100)
                        : 0}%
                    </p>
                    <p className="text-xs text-slate-500">Interviews completed</p>
                  </div>
                  <div className="h-8 w-8 bg-purple-100 rounded-lg flex items-center justify-center">
                    <CheckCircle className="h-4 w-4 text-purple-600" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-white rounded-xl shadow-sm border border-slate-200">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-slate-600">Hire Rate</p>
                    <p className="text-2xl font-bold text-slate-900">
                      {interviews.length > 0 
                        ? Math.round((interviews.filter(i => i.recommendation === 'hire' || i.recommendation === 'strong_hire').length / interviews.filter(i => i.status === 'completed').length) * 100)
                        : 0}%
                    </p>
                    <p className="text-xs text-slate-500">Of completed interviews</p>
                  </div>
                  <div className="h-8 w-8 bg-green-100 rounded-lg flex items-center justify-center">
                    <Award className="h-4 w-4 text-green-600" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Charts and Detailed Analytics */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Recommendation Distribution */}
            <Card className="bg-white rounded-xl shadow-sm border border-slate-200">
              <CardHeader>
                <CardTitle className="text-lg font-semibold text-slate-900 flex items-center">
                  <BarChart3 className="h-5 w-5 mr-2 text-slate-600" />
                  Recommendation Distribution
                </CardTitle>
              </CardHeader>
              <CardContent>
                {(() => {
                  const recommendations = interviews.reduce((acc, interview) => {
                    const rec = interview.recommendation || 'unknown';
                    acc[rec] = (acc[rec] || 0) + 1;
                    return acc;
                  }, {} as Record<string, number>);

                  const total = Object.values(recommendations).reduce((sum, count) => sum + count, 0);

                  if (total === 0) {
                    return (
                      <div className="text-center py-8">
                        <BarChart3 className="h-12 w-12 text-slate-400 mx-auto mb-4" />
                        <p className="text-slate-600">No interview data available</p>
                      </div>
                    );
                  }

                  return (
                    <div className="space-y-4">
                      {Object.entries(recommendations).map(([rec, count]) => {
                        const percentage = (count / total) * 100;
                        const colors = {
                          'strong_hire': 'bg-green-500',
                          'hire': 'bg-blue-500', 
                          'maybe': 'bg-yellow-500',
                          'no_hire': 'bg-red-500',
                          'unknown': 'bg-gray-500'
                        };
                        
                        return (
                          <div key={rec} className="space-y-2">
                            <div className="flex justify-between items-center">
                              <span className="text-sm font-medium text-slate-700 capitalize">
                                {rec.replace('_', ' ')}
                              </span>
                              <span className="text-sm text-slate-600">{count} ({percentage.toFixed(0)}%)</span>
                            </div>
                            <div className="w-full bg-slate-200 rounded-full h-2">
                              <div 
                                className={`h-2 rounded-full ${colors[rec as keyof typeof colors] || 'bg-gray-500'}`}
                                style={{ width: `${percentage}%` }}
                              />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  );
                })()}
              </CardContent>
            </Card>


          </div>

          {/* Performance Over Time */}
          <Card className="bg-white rounded-xl shadow-sm border border-slate-200">
            <CardHeader>
              <CardTitle className="text-lg font-semibold text-slate-900 flex items-center">
                <Clock className="h-5 w-5 mr-2 text-slate-600" />
                Interview Performance Trends
              </CardTitle>
            </CardHeader>
            <CardContent>
              {(() => {
                if (interviews.length === 0) {
                  return (
                    <div className="text-center py-8">
                      <Clock className="h-12 w-12 text-slate-400 mx-auto mb-4" />
                      <p className="text-slate-600">No interview trends available</p>
                      <p className="text-sm text-slate-500 mt-1">Complete more interviews to see trends</p>
                    </div>
                  );
                }

                // Group interviews by month
                const monthlyData = interviews.reduce((acc, interview) => {
                  const date = new Date(interview.createdAt);
                  const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
                  
                  if (!acc[monthKey]) {
                    acc[monthKey] = {
                      total: 0,
                      completed: 0,
                      averageRating: 0,
                      hires: 0,
                      ratings: []
                    };
                  }
                  
                  acc[monthKey].total++;
                  if (interview.status === 'completed') {
                    acc[monthKey].completed++;
                    acc[monthKey].ratings.push(interview.overallRating);
                  }
                  if (interview.recommendation === 'hire' || interview.recommendation === 'strong_hire') {
                    acc[monthKey].hires++;
                  }
                  
                  return acc;
                }, {} as Record<string, any>);

                // Calculate averages
                Object.values(monthlyData).forEach((month: any) => {
                  if (month.ratings.length > 0) {
                    month.averageRating = month.ratings.reduce((sum: number, r: number) => sum + r, 0) / month.ratings.length;
                  }
                });

                const sortedMonths = Object.entries(monthlyData).sort(([a], [b]) => a.localeCompare(b));

                return (
                  <div className="space-y-6">
                    {sortedMonths.map(([month, data]: [string, any]) => {
                      const [year, monthNum] = month.split('-');
                      const monthName = new Date(parseInt(year), parseInt(monthNum) - 1).toLocaleDateString('en-US', { 
                        month: 'long', 
                        year: 'numeric' 
                      });

                      return (
                        <div key={month} className="border border-slate-200 rounded-lg p-4">
                          <h4 className="font-semibold text-slate-900 mb-3">{monthName}</h4>
                          <div className="grid grid-cols-3 gap-4">
                            <div className="text-center">
                              <p className="text-2xl font-bold text-slate-900">
                                {data.completed > 0 ? Math.round((data.hires / data.completed) * 100) : 0}%
                              </p>
                              <p className="text-xs text-slate-600">Conversion Rate</p>
                            </div>
                            <div className="text-center">
                              <p className="text-2xl font-bold text-slate-900">
                                {(() => {
                                  const interviewsThisMonth = interviews.filter(i => {
                                    const date = new Date(i.createdAt);
                                    const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
                                    return monthKey === month;
                                  });
                                  const strongHires = interviewsThisMonth.filter(i => i.recommendation === 'strong_hire').length;
                                  return strongHires;
                                })()}
                              </p>
                              <p className="text-xs text-slate-600">Strong Hires</p>
                            </div>
                            <div className="text-center">
                              <p className="text-2xl font-bold text-slate-900">
                                {(() => {
                                  const interviewsThisMonth = interviews.filter(i => {
                                    const date = new Date(i.createdAt);
                                    const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
                                    return monthKey === month && i.status === 'completed';
                                  });
                                  const types = interviewsThisMonth.reduce((acc, i) => {
                                    acc[i.type] = (acc[i.type] || 0) + 1;
                                    return acc;
                                  }, {} as Record<string, number>);
                                  const topType = Object.entries(types).sort(([,a], [,b]) => b - a)[0];
                                  return topType?.[0]?.charAt(0).toUpperCase() + (topType?.[0]?.slice(1) || 'None');
                                })()}
                              </p>
                              <p className="text-xs text-slate-600">Top Interview Type</p>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                );
              })()}
            </CardContent>
          </Card>

          {/* Top Performing Candidates */}
          <Card className="bg-white rounded-xl shadow-sm border border-slate-200">
            <CardHeader>
              <CardTitle className="text-lg font-semibold text-slate-900 flex items-center">
                <Award className="h-5 w-5 mr-2 text-slate-600" />
                Top Performing Candidates
              </CardTitle>
            </CardHeader>
            <CardContent>
              {(() => {
                const topCandidates = interviews
                  .filter(i => i.status === 'completed')
                  .sort((a, b) => b.overallRating - a.overallRating)
                  .slice(0, 10);

                if (topCandidates.length === 0) {
                  return (
                    <div className="text-center py-8">
                      <Award className="h-12 w-12 text-slate-400 mx-auto mb-4" />
                      <p className="text-slate-600">No completed interviews yet</p>
                      <p className="text-sm text-slate-500 mt-1">Complete interviews to see top performers</p>
                    </div>
                  );
                }

                return (
                  <div className="space-y-4">
                    {topCandidates.map((interview, index) => (
                      <div key={interview.id} className="flex items-center justify-between p-4 border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors">
                        <div className="flex items-center gap-4">
                          <div className="flex items-center justify-center w-8 h-8 bg-slate-100 rounded-full text-sm font-semibold text-slate-700">
                            #{index + 1}
                          </div>
                          <div>
                            <h4 className="font-medium text-slate-900">{interview.candidateName}</h4>
                            <p className="text-sm text-slate-600">{interview.position}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-4">
                          <Badge className={cn("text-xs font-medium border", getRecommendationColor(interview.recommendation))}>
                            {interview.recommendation.replace('_', ' ')}
                          </Badge>
                          <div className="flex items-center gap-1">
                            <Star className="h-4 w-4 text-yellow-500 fill-current" />
                            <span className="font-bold text-slate-900">{interview.overallRating.toFixed(1)}</span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                );
              })()}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Modals */}
      {showCandidateSelector && (
        <CandidateSelector
          onSelect={handleCandidateSelected}
          onCancel={() => setShowCandidateSelector(false)}
          projectId={projectId}
        />
      )}
      
      {showInterviewForm && (
        <InterviewForm
          onSave={handleInterviewSave}
          onCancel={() => setShowInterviewForm(false)}
          interview={selectedInterview}
          candidate={selectedCandidate}
        />
      )}

      {showInterviewResults && selectedInterview && (
        <InterviewResults
          interview={selectedInterview}
          onClose={() => setShowInterviewResults(false)}
        />
      )}
    </div>
  );
} 